import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Award, ChevronRight } from 'lucide-react';

const certifications = [
  { id: 'bridge', title: 'Bridge Inspection', images: ['/Experience/BRIDGE1.jpg', '/Experience/BRIDGE2.jpg'] },
  { id: 'contract', title: 'Contract Admin', images: ['/Experience/contract3.jpg', '/Experience/contract4.jpg'] },
  { id: 'environmental', title: 'Environmental', images: ['/Experience/environmental1.jpg','/Experience/environmental2.jpg'] },
  { id: 'highway', title: 'Highway Design', images: ['/Experience/highway1.jpg', '/Experience/highway2.jpg' , '/Experience/highway3.jpg', '/Experience/highway4.jpg', '/Experience/highway5.jpg', '/Experience/highway6.jpg', '/Experience/highway7.jpg', '/Experience/highway8.jpg', '/Experience/highway9.jpg', '/Experience/highway10.jpg', '/Experience/highway11.jpg'] },
  { id: 'iso', title: 'ISO Standards', images: ['/Experience/iso1.jpg', '/Experience/iso2.jpg', '/Experience/iso3.jpg'] },
  { id: 'microstation', title: 'Microstation', images: ['/Experience/microstation1.jpg'] },
  { id: 'qa-qc', title: 'QA/QC & Construction', images: ['/Experience/qa-qc1.jpg', '/Experience/qa-qc2.jpg', '/Experience/qa-qc3.jpg', '/Experience/qa-qc4.jpg', '/Experience/qa-qc5.jpg', '/Experience/qa-qc6.jpg', '/Experience/qa-qc7.jpg'] },
  { id: 'risk', title: 'Risk Management', images: ['/Experience/risk1.jpg'] },
  { id: 'site', title: 'Site Engineering', images: ['/Experience/site1.jpg', '/Experience/site2.jpg', '/Experience/site3.jpg', '/Experience/site4.jpg'] },
  { id: 'soil', title: 'Soil & Foundation', images: ['/Experience/soil1.jpg'] },
  { id: 'value', title: 'Value Engineering', images: ['/Experience/value.jpg', '/Experience/value2.jpg'] },
];

const Certifications = () => {
  const sectionRefs = useRef({});

  const scrollToSection = (id) => {
    const element = sectionRefs.current[id];
    if (element) {
      const offset = 100; // Offset for sticky header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Certifications | Sooners Land LLC</title>
        <meta name="description" content="Professional certifications and expertise of Sooners Land LLC in bridge inspection, highway design, and more." />
      </Helmet>

      {/* Header */}
      <section className="bg-primary pt-32 pb-16 px-6 md:px-12 text-center overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full bg-accent/5 -skew-y-3 origin-top-left" />
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-accent-soft rounded-2xl mb-6"
          >
            <Award className="text-accent" size={32} />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Professional Credentials</h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            A comprehensive record of our specialized expertise and industry-standard certifications.
          </p>
        </div>
      </section>

      {/* Sticky Chip Navigation */}
      <div className="sticky top-[72px] z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 py-4 overflow-x-auto no-scrollbar shadow-sm">
        <div className="container mx-auto px-6 whitespace-nowrap flex gap-3">
          {certifications.map((cert) => (
            <button
              key={cert.id}
              onClick={() => scrollToSection(cert.id)}
              className="px-5 py-2 rounded-full bg-slate-100 text-slate-600 text-sm font-bold hover:bg-accent hover:text-white transition-all duration-300 border border-slate-200"
            >
              {cert.title}
            </button>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 py-16">
        {certifications.map((cert) => (
          <motion.div 
            key={cert.id} 
            ref={(el) => (sectionRefs.current[cert.id] = el)} 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            className="mb-20 last:mb-0"
          >
            <div className="flex items-center gap-4 mb-8">
               <div className="h-0.5 flex-grow bg-slate-200" />
               <h2 className="text-2xl md:text-3xl font-bold text-primary px-4">
                  {cert.title}
               </h2>
               <div className="h-0.5 flex-grow bg-slate-200" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cert.images.map((img, index) => (
                <div key={index} className="glass-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group border border-slate-100">
                  <div className="relative h-64 md:h-80 overflow-hidden">
                    <img 
                      src={img} 
                      alt={`${cert.title} - ${index + 1}`} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                    />
                    <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-4 bg-white flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-widest">Certificate {index + 1}</span>
                    <Award size={16} className="text-accent" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Certifications;
